import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { notificationAPI, attendanceAPI } from "../lib/api";
import { cn, getInitials, getCurrentPosition } from "../lib/utils";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Building2,
  Users,
  Network,
  Briefcase,
  Clock,
  MapPin,
  FileText,
  DollarSign,
  Receipt,
  Megaphone,
  CalendarDays,
  UserMinus,
  Bell,
  Settings,
  LogOut,
  CreditCard,
  Menu,
  X,
  ChevronDown,
  Shield,
  ClipboardList,
  LogIn,
  LogOut as LogOutIcon,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";

const DashboardLayout = () => {
  const { user, logout, isSuperAdmin, isHRAdmin, isManager } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Attendance state
  const [attendanceStatus, setAttendanceStatus] = useState(null); // null, 'checked_in', 'checked_out'
  const [checkInTime, setCheckInTime] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationAPI.getUnreadCount();
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch today's attendance status
  useEffect(() => {
    const fetchTodayAttendance = async () => {
      try {
        const response = await attendanceAPI.getToday();
        if (response.data.check_in_time) {
          setCheckInTime(response.data.check_in_time);
          if (response.data.check_out_time) {
            setAttendanceStatus('checked_out');
          } else {
            setAttendanceStatus('checked_in');
          }
        } else {
          setAttendanceStatus(null);
        }
      } catch (error) {
        console.error("Failed to fetch attendance:", error);
      }
    };

    fetchTodayAttendance();
  }, []);

  const handleCheckIn = async () => {
    setAttendanceLoading(true);
    try {
      toast.info("Fetching your location...");
      const pos = await getCurrentPosition();
      const response = await attendanceAPI.checkIn({ ...pos, source: "web" });
      setAttendanceStatus('checked_in');
      setCheckInTime(response.data.check_in_time);
      toast.success("Checked in successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to check in");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setAttendanceLoading(true);
    try {
      let pos = {};
      try {
        pos = await getCurrentPosition();
      } catch (e) {
        // location optional for check-out
      }
      await attendanceAPI.checkOut({ ...pos, source: "web" });
      setAttendanceStatus('checked_out');
      toast.success("Checked out successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to check out");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Navigation items based on role + tenant's plan modules
  const getNavItems = () => {
    const items = [
      { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", roles: ["all"] },
    ];

    if (isSuperAdmin) {
      items.push(
        { icon: Building2, label: "Tenants", path: "/tenants", roles: ["super_admin"] },
        { icon: CreditCard, label: "Subscription Plans", path: "/subscription-plans", roles: ["super_admin"] }
      );
    }

    if (isSuperAdmin || isHRAdmin) {
      items.push(
        { icon: Users, label: "Users", path: "/users", module: "users" },
        { icon: Network, label: "Departments", path: "/departments", module: "departments" },
        { icon: Briefcase, label: "Designations", path: "/designations", module: "designations" },
        { icon: MapPin, label: "Locations", path: "/locations", module: "locations" },
        { icon: CalendarDays, label: "Holidays & Calendar", path: "/holidays", module: "holidays" }
      );
    }

    items.push(
      { icon: Clock, label: "Attendance", path: "/attendance", module: "attendance" }
    );

    if (isSuperAdmin || isHRAdmin || isManager) {
      items.push(
        { icon: ClipboardList, label: "Attendance Reports", path: "/attendance/reports", module: "attendance_reports" }
      );
    }

    items.push(
      { icon: CalendarDays, label: "Leaves", path: "/leaves", module: "leaves" }
    );

    if (isHRAdmin) {
      items.push(
        { icon: Shield, label: "Leave Policies", path: "/leaves/policies", module: "leaves" }
      );
    }

    items.push(
      { icon: FileText, label: "Documents", path: "/documents", module: "documents" }
    );

    if (isHRAdmin) {
      items.push(
        { icon: DollarSign, label: "Salary Structures", path: "/salary/structures", module: "salary" }
      );
    }

    items.push(
      { icon: Receipt, label: "Salary Slips", path: "/salary/slips", module: "salary" },
      { icon: Megaphone, label: "Notices", path: "/notices", module: "notices" },
      { icon: UserMinus, label: "Resignations", path: "/resignations", module: "resignations" }
    );

    if (isSuperAdmin || isHRAdmin) {
      items.push(
        { icon: ClipboardList, label: "Audit Logs", path: "/audit-logs", module: "audit_logs" }
      );
    }

    // Plan-based module gating (SuperAdmin always sees everything; legacy/empty = show all)
    const enabledModules = user?.enabled_modules;
    return items.filter((item) => {
      if (isSuperAdmin) return true;
      if (!item.module) return true;
      if (!Array.isArray(enabledModules) || enabledModules.length === 0) return true;
      return enabledModules.includes(item.module);
    });
  };

  const navItems = getNavItems();

  const NavItem = ({ item, collapsed }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        className={cn(
          "nav-item",
          isActive && "nav-item-active",
          collapsed && "justify-center px-3"
        )}
        onClick={() => setMobileMenuOpen(false)}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="app-container flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "sidebar",
          !sidebarOpen && "lg:w-20",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          {sidebarOpen ? (
            <Link to="/dashboard" className="flex items-center" data-testid="sidebar-logo">
              <img src="/empulse-logo.png" alt="EmpulseBit" className="h-8 w-auto" />
            </Link>
          ) : (
            <Link to="/dashboard" className="mx-auto" data-testid="sidebar-logo-icon">
              <img src="/empulse-icon.png" alt="EmpulseBit" className="h-8 w-8 object-contain" />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={!sidebarOpen} />
          ))}
        </nav>

        {/* Sidebar Toggle (Desktop) */}
        <div className="hidden lg:block p-4 border-t border-slate-200">
          <Button
            variant="ghost"
            className="w-full justify-center"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "main-content flex-1",
          !sidebarOpen && "lg:ml-20"
        )}
      >
        {/* Top Bar */}
        <header className="topbar flex items-center justify-between" data-testid="topbar">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-semibold text-slate-900">
                {navItems.find((item) => item.path === location.pathname)?.label || "Dashboard"}
              </h1>
              <p className="text-sm text-slate-500">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Check In / Check Out Button - Only for HR Admin and Manager */}
            {(isHRAdmin || isManager) && (
              <div className="flex items-center gap-2">
                {attendanceStatus === null && (
                  <Button
                    onClick={handleCheckIn}
                    disabled={attendanceLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="check-in-btn"
                  >
                    {attendanceLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <LogIn className="w-4 h-4 mr-2" />
                    )}
                    Check In
                  </Button>
                )}
                {attendanceStatus === 'checked_in' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 hidden md:block">
                      In: {checkInTime ? new Date(checkInTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                    </span>
                    <Button
                      onClick={handleCheckOut}
                      disabled={attendanceLoading}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="check-out-btn"
                    >
                      {attendanceLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <LogOutIcon className="w-4 h-4 mr-2" />
                      )}
                      Check Out
                    </Button>
                  </div>
                )}
                {attendanceStatus === 'checked_out' && (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                    <Clock className="w-3 h-3 mr-1" />
                    Day Complete
                  </Badge>
                )}
              </div>
            )}

            {/* Notifications */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => navigate("/notifications")}
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2"
                  data-testid="user-menu-btn"
                >
                  <div className="avatar">
                    {getInitials(user?.first_name, user?.last_name)}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-slate-500 capitalize">
                      {user?.role?.replace("_", " ")}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.email}</p>
                  <Badge variant="secondary" className="mt-1 capitalize">
                    {user?.role?.replace("_", " ")}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-item">
                  <Settings className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="notifications-menu-item">
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                  {unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-auto">{unreadCount}</Badge>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-container">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
